import { useRef, useCallback, useEffect } from 'react';

const services = [
  {
    icon: 'ri-flashlight-line',
    title: 'Électricité',
    items: ['Tableaux électriques NIBT/OIBT', 'Bornes de recharge VE (Wallbox 22 kW)', 'Dépannage urgence 24/7', 'Domotique KNX & Philips Hue'],
  },
  {
    icon: 'ri-fire-line',
    title: 'Pompes à Chaleur',
    items: ['PAC air/eau & géothermique', 'Remplacement chaudières', 'Subventions jusqu\'à CHF 10\'000', 'ELCO, Viessmann, Daikin'],
  },
  {
    icon: 'ri-drop-line',
    title: 'Plomberie & Sanitaire',
    items: ['Urgences fuites & dégâts des eaux', 'Salles de bain complètes', 'Douches italiennes & baignoires', 'Geberit, Hansgrohe, Grohe'],
  },
  {
    icon: 'ri-windy-line',
    title: 'Ventilation & Clim',
    items: ['VMC double flux Zehnder & Helios', 'Climatisation réversible multi-split', 'Entretien & maintenance VMC', 'Conformité normes SIA 382'],
  },
  {
    icon: 'ri-home-wifi-line',
    title: 'Domotique',
    items: ['Systèmes KNX & Loxone', 'Éclairage intelligent Philips Hue', 'Volets, stores & contrôle d\'accès', 'Apple HomeKit / Google Home'],
  },
  {
    icon: 'ri-tools-line',
    title: 'Maintenance 24/7',
    items: ['Intervention en 30 à 90 min', 'Pannes électriques, eau, chauffage', 'Contrats maintenance préventive', 'Gestion régies & copropriétés'],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Entreprise Générale',
    items: ['Coordination tous corps de métier', 'Rénovation complète clé en main', 'Gestion de projet & suivi chantier', 'Devis détaillé & planning précis'],
  },
  {
    icon: 'ri-brush-line',
    title: 'Nettoyage & Conciergerie',
    items: ['Nettoyage fin de chantier', 'Entretien bureaux & immeubles', 'Nettoyage vitres & façades', 'Débarras & évacuation déchets'],
  },
];

const stats = [
  { value: '19+', label: 'Années d\'expérience' },
  { value: '2 500+', label: 'Clients satisfaits' },
  { value: '24/7', label: 'Disponibilité urgences' },
  { value: '8', label: 'Corps de métier' },
];

const certifs = [
  'Certifié NIBT/OIBT',
  'Qualité ISO 9001',
  'Partenaire SuisseEnergie',
  'Agréé cantons GE, VD, FR',
];

const zones = [
  'Genève', 'Meyrin', 'Vernier', 'Lancy', 'Carouge', 'Onex',
  'Thônex', 'Chêne-Bougeries', 'Plan-les-Ouates', 'Bernex',
  'Satigny', 'Nyon', 'Lausanne', 'Vaud', 'Fribourg',
];

const references = [
  { name: 'Régies immobilières', desc: 'Partenaires de confiance pour la gestion technique de leurs immeubles' },
  { name: 'Hôtels & Hôtellerie', desc: 'Maintenance préventive et interventions urgentes 24/7' },
  { name: 'Entreprises & Bureaux', desc: 'Installations électriques, climatisation et domotique' },
  { name: 'Particuliers', desc: 'Rénovations complètes, salles de bain, cuisines, chauffage' },
];

export default function PresentationBrochure() {
  const brochureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'hb-global-print-style';
    styleEl.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #brochure-print, #brochure-print * { visibility: visible !important; }
        #brochure-print {
          position: fixed !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          z-index: 99999 !important;
          background: white !important;
        }
        .no-print { display: none !important; }
        @page { margin: 0; size: A4; }
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      const el = document.getElementById('hb-global-print-style');
      if (el) el.remove();
    };
  }, []);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  return (
    <section id="pdf-viewer" className="py-16 bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        {/* Header toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 no-print">
          <div>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-1">
              Notre plaquette de présentation
            </h2>
            <p className="text-gray-500 text-sm">
              Consultez et imprimez notre plaquette complète directement depuis votre navigateur.
            </p>
          </div>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-bold px-6 py-3 rounded-xl transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-printer-line text-lg"></i>
            Imprimer / Sauvegarder en PDF
          </button>
        </div>

        {/* Brochure */}
        <div
          id="brochure-print"
          ref={brochureRef}
          className="bg-white rounded-2xl overflow-hidden border border-gray-100"
        >
          {/* ── PAGE 1 : COVER ── */}
          <div className="relative min-h-[480px] flex flex-col justify-between overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 60%, #3d3d3d 100%)' }}>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
              style={{ background: 'radial-gradient(circle, #f97c0a 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5"
              style={{ background: 'radial-gradient(circle, #f97c0a 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }}></div>

            <div className="relative z-10 p-12 flex-1 flex flex-col justify-center">
              {/* Logo area */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-14 h-14 flex items-center justify-center bg-brand-500 rounded-2xl shrink-0">
                  <i className="ri-tools-fill text-white text-2xl"></i>
                </div>
                <div>
                  <h1 className="text-3xl font-black text-white tracking-tight">CasaReNovaPlus</h1>
                  <p className="text-brand-400 text-sm font-semibold tracking-widest uppercase">Rénovation · Neuenegg</p>
                </div>
              </div>

              <div className="max-w-2xl">
                <div className="inline-block bg-brand-500/20 border border-brand-500/30 text-brand-300 text-xs font-bold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
                  Plaquette de présentation 2026
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-5">
                  Votre partenaire<br />
                  <span className="text-brand-400">rénovation</span><br />
                  à Neuenegg
                </h2>
                <p className="text-gray-400 text-base leading-relaxed max-w-xl">
                  Rénovation · Électricité · Plomberie · Chauffage · Ventilation · Domotique · Maintenance 24/7 · Entreprise Générale
                </p>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="relative z-10 border-t border-white/10 px-12 py-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <i className="ri-map-pin-line text-brand-400"></i>
                  Austrasse 95, 3176 Neuenegg
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <i className="ri-phone-line text-brand-400"></i>
                  +41 76 480 31 88 / +41 76 565 42 03
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <i className="ri-mail-line text-brand-400"></i>
                  info@casarenovaplus.ch
                </div>
              </div>
              <p className="text-gray-500 text-xs">www.casarenovaplus.ch</p>
            </div>
          </div>

          {/* ── SECTION : QUI SOMMES-NOUS ── */}
          <div className="px-12 py-10 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
              <h3 className="text-xl font-black text-gray-900 uppercase tracking-wide">Qui sommes-nous</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Fondée à Neuenegg, <strong>CasaReNovaPlus</strong> est une entreprise multi-technique qui accompagne particuliers, entreprises et régies dans tous leurs projets de rénovation, d'installation et de maintenance. Notre équipe de techniciens certifiés intervient rapidement dans tout le canton de Fribourg et en Suisse romande.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Notre force : <strong>un seul interlocuteur pour tous vos corps de métier</strong>. Électricité, plomberie, chauffage, ventilation, domotique — nous coordonnons tout pour vous simplifier la vie et garantir des travaux de qualité dans les délais impartis.
                </p>
                <div className="flex flex-wrap gap-2">
                  {certifs.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-brand-100">
                      <i className="ri-shield-check-line text-brand-500"></i>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                    <p className="text-2xl font-black text-brand-500">{s.value}</p>
                    <p className="text-xs text-gray-500 mt-1 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── SECTION : NOS PRESTATIONS ── */}
          <div className="px-12 py-10 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
              <h3 className="text-xl font-black text-gray-900 uppercase tracking-wide">Nos prestations</h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {services.map((s) => (
                <div key={s.title} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-brand-500/10 rounded-lg shrink-0">
                      <i className={`${s.icon} text-brand-500 text-sm`}></i>
                    </div>
                    <h4 className="text-xs font-black text-gray-900 leading-tight">{s.title}</h4>
                  </div>
                  <ul className="space-y-1">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-start gap-1.5 text-xs text-gray-500">
                        <i className="ri-check-line text-brand-500 mt-0.5 shrink-0 text-xs"></i>
                        <span className="leading-tight">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION : RÉFÉRENCES & ZONES ── */}
          <div className="px-12 py-10 border-b border-gray-100">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Références */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
                  <h3 className="text-xl font-black text-gray-900 uppercase tracking-wide">Nos clients</h3>
                </div>
                <div className="space-y-3">
                  {references.map((r) => (
                    <div key={r.name} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="w-8 h-8 flex items-center justify-center bg-brand-500 rounded-lg shrink-0 mt-0.5">
                        <i className="ri-building-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{r.name}</p>
                        <p className="text-xs text-gray-500 leading-relaxed">{r.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Zones */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
                  <h3 className="text-xl font-black text-gray-900 uppercase tracking-wide">Zones d'intervention</h3>
                </div>
                <p className="text-gray-500 text-sm mb-4 leading-relaxed">
                  Nous intervenons dans tout le canton de Fribourg et en Suisse romande. Nos équipes sont basées à Neuenegg pour une réactivité maximale.
                </p>
                <div className="flex flex-wrap gap-2">
                  {zones.map((z) => (
                    <span key={z} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200">
                      <i className="ri-map-pin-2-line text-brand-500 text-xs"></i>
                      {z}
                    </span>
                  ))}
                </div>
                <div className="mt-5 bg-brand-50 border border-brand-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-alarm-warning-line text-brand-500"></i>
                    <p className="text-sm font-bold text-brand-700">Urgences 24h/24 — 7j/7</p>
                  </div>
                  <p className="text-xs text-brand-600 leading-relaxed">
                    Intervention garantie en 30 à 90 minutes sur Genève et communes limitrophes. Astreinte permanente pour pannes électriques, fuites d'eau et urgences chauffage.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── SECTION : POURQUOI NOUS CHOISIR ── */}
          <div className="px-12 py-10 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
              <h3 className="text-xl font-black text-gray-900 uppercase tracking-wide">Pourquoi choisir CasaReNovaPlus ?</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: 'ri-user-star-line', title: 'Un seul interlocuteur', desc: 'Fini les multiples artisans à coordonner. Nous gérons tout de A à Z.' },
                { icon: 'ri-time-line', title: 'Réactivité garantie', desc: 'Devis sous 24h, intervention rapide, respect des délais contractuels.' },
                { icon: 'ri-shield-check-line', title: 'Certifié & assuré', desc: 'Tous nos techniciens sont certifiés. Travaux garantis et assurés RC.' },
                { icon: 'ri-price-tag-3-line', title: 'Devis gratuit & transparent', desc: 'Pas de mauvaises surprises. Devis détaillé avant chaque intervention.' },
                { icon: 'ri-leaf-line', title: 'Solutions durables', desc: 'Partenaire SuisseEnergie, nous privilégions les solutions économes en énergie.' },
                { icon: 'ri-customer-service-2-line', title: 'Service après-vente', desc: 'Suivi post-intervention, contrats de maintenance, hotline dédiée.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-brand-500/10 rounded-lg shrink-0">
                    <i className={`${item.icon} text-brand-500`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 mb-1">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION : CONTACT ── */}
          <div style={{ background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' }} className="px-12 py-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-brand-500 rounded-full"></div>
              <h3 className="text-xl font-black text-white uppercase tracking-wide">Nous contacter</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { icon: 'ri-map-pin-line', label: 'Adresse', value: 'Austrasse 95\n3176 Neuenegg, Fribourg' },
                { icon: 'ri-phone-line', label: 'Téléphone', value: 'Filipe: +41 76 480 31 88\nFrancisco: +41 76 565 42 03' },
                { icon: 'ri-mail-line', label: 'Email', value: 'info@casarenovaplus.ch\nRéponse sous 24h' },
                { icon: 'ri-global-line', label: 'Site web', value: 'www.casarenovaplus.ch\nDevis en ligne' },
              ].map((c) => (
                <div key={c.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-brand-500/20 rounded-lg shrink-0">
                      <i className={`${c.icon} text-brand-400 text-sm`}></i>
                    </div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{c.label}</p>
                  </div>
                  <p className="text-sm text-white font-semibold whitespace-pre-line leading-relaxed">{c.value}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-gray-500 text-xs">
                © 2026 CasaReNovaPlus Sàrl · Tous droits réservés · CHE-XXX.XXX.XXX TVA
              </p>
              <p className="text-gray-500 text-xs">
                Document confidentiel — Plaquette de présentation CasaReNovaPlus 2026
              </p>
            </div>
          </div>
        </div>

        {/* Bottom action bar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 no-print">
          <p className="text-gray-400 text-sm">
            <i className="ri-information-line mr-1"></i>
            Cliquez sur "Imprimer" puis choisissez <strong>"Enregistrer en PDF"</strong> dans votre navigateur.
          </p>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-5 py-2.5 rounded-xl transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-download-line"></i>
            Télécharger en PDF
          </button>
        </div>
      </div>
    </section>
  );
}
